import React, { useEffect, useMemo, useState, useRef } from "react";
import AutoAwesomeIcon        from "@mui/icons-material/AutoAwesome";
import CheckIcon              from "@mui/icons-material/Check";
import SmartToyOutlinedIcon   from "@mui/icons-material/SmartToyOutlined";
import ArticleOutlinedIcon    from "@mui/icons-material/ArticleOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import { useGetUserDetailsMutation } from "../../../redux/api/authApi";
import RohanImg       from "../../../assets/podcast_monster/rohan_png.png";
import Ocesha_header  from "pages/ocesha_landing/ocesha_header";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../redux/store";
import { logout } from "redux/features/userSlice";
import { toast } from "react-toastify";
import { getCookie, removeCookie } from "../../../utils/storageUtils";
import axios from "axios";
import { useTTSQueue } from "hooks/useTTSQueue";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import SendIcon from "@mui/icons-material/Send";
import {
  useAuthorityEngineSuggestionsTriggerMutation,
  useLazyGetTaskStatusQuery,
  useGetVideoSuggestionsTriggerQuery,
  useGeneratePostMutation,
  useGetBlogSuggestionsTriggerQuery,
  useGenericEngineSuggestionsTriggerQuery,
} from "../../../redux/api/promptApi";
import {
  Grid, SxProps, Theme, Button, IconButton, Typography, Box,
  Stack, Divider, CircularProgress, TextareaAutosize, Paper,
} from "@mui/material";
import VolumeUpIcon  from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import { Skeleton } from "@mui/material";
import LinkedInIcon            from "@mui/icons-material/LinkedIn";
import FacebookIcon            from "@mui/icons-material/Facebook";
import TwitterIcon             from "@mui/icons-material/Twitter";
import YouTubeIcon             from "@mui/icons-material/YouTube";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CampaignOutlinedIcon    from "@mui/icons-material/CampaignOutlined";
import { saveVoiceSample, getVoiceSamples, base64ToFile, clearVoiceSamples } from "utils/digitalTwinVoice";

// ─── Types ────────────────────────────────────────────────────────────────────

interface GenerationStep { id: number; label: string; delay: number; }
interface Particle { id: number; x: number; color: string; delay: number; size: number; }

// ─── Constants ────────────────────────────────────────────────────────────────

const GENERATION_STEPS: GenerationStep[] = [
  { id: 1, label: "Analyzing your answers",       delay: 800  },
  { id: 2, label: "Understanding your expertise", delay: 1800 },
  { id: 3, label: "Generating content ideas",     delay: 2800 },
  { id: 4, label: "Crafting your first content",  delay: 0    },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const DarkCard = ({ children, sx }: { children: React.ReactNode; sx?: object }) => (
  <Box sx={{
    background: "#15151D", borderRadius: "14px",
    border: "1px solid rgba(255,255,255,0.07)", overflow: "hidden", p: 2.5, ...sx,
  }}>
    {children}
  </Box>
);

const SuggestedCardSkeleton = () => (
  <DarkCard sx={{ p: 2 }}>
    <Stack spacing={1.5}>
      <Skeleton variant="text" width="60%" height={20} />
      <Skeleton variant="text" width="100%" height={16} />
      <Skeleton variant="text" width="80%" height={16} />
      <Skeleton variant="rectangular" height={36} sx={{ borderRadius: "8px" }} />
    </Stack>
  </DarkCard>
);

const GradientBtn = ({ label, onClick, loading = false, disabled = false }: {
  label: string; onClick?: () => void; loading?: boolean; disabled?: boolean;
}) => (
  <Button fullWidth variant="contained" onClick={onClick} disabled={disabled || loading}
    sx={{
      background: "linear-gradient(90deg,#0168B5,#4BA5EB)", textTransform: "none",
      color: "#fff", borderRadius: "8px", fontSize: "13px", fontWeight: 500,
      boxShadow: "none", py: 1,
      "&:hover": { background: "linear-gradient(90deg,#0158a0,#3a94da)", boxShadow: "none" },
    }}
  >
    {loading ? "Generating..." : label}
  </Button>
);

const Confetti: React.FC = () => {
  const [done, setDone] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDone(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  if (done) return null;

  const COLORS = ["#FFC107","#4BA5EB","#8b83ff","#22c55e","#f97316","#ec4899"];
  const particles: Particle[] = Array.from({ length: 28 }, (_, i) => ({
    id: i, x: 20 + Math.random() * 60,
    color: COLORS[i % COLORS.length], delay: Math.random() * 0.8, size: 6 + Math.random() * 6,
  }));
  return (
    <div style={{ position:"relative", height:60, width:"100%", overflow:"hidden", marginBottom:8 }}>
      {particles.map(p => (
        <div key={p.id} style={{
          position:"absolute", left:`${p.x}%`, top:0, width:p.size, height:p.size,
          borderRadius: p.id % 3 === 0 ? "50%" : 2, background: p.color,
          animation: `confettiFall 2s ${p.delay}s ease-in forwards`, opacity:0,
          transform: `rotate(${Math.random() * 360}deg)`,
        }} />
      ))}
    </div>
  );
};

const ChalkboardIcon = ({ sx }: { sx?: SxProps<Theme> }) => (
  <Box component="svg" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"
    sx={{ width:16, height:16, display:"block", ...sx }}>
    <path d="M8.125 13.75C9.506 13.75 10.625 12.631 10.625 11.25C10.625 9.869 9.506 8.75 8.125 8.75C6.744 8.75 5.625 9.869 5.625 11.25C5.625 12.631 6.744 13.75 8.125 13.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4.172 16.25C4.527 15.502 5.086 14.87 5.786 14.427C6.486 13.984 7.297 13.749 8.125 13.749C8.953 13.749 9.764 13.984 10.464 14.427C11.164 14.87 11.723 15.502 12.078 16.25H16.875C17.041 16.25 17.2 16.184 17.317 16.067C17.434 15.95 17.5 15.791 17.5 15.625V4.375C17.5 4.209 17.434 4.05 17.317 3.933C17.2 3.816 17.041 3.75 16.875 3.75H3.125C2.959 3.75 2.8 3.816 2.683 3.933C2.566 4.05 2.5 4.209 2.5 4.375V15.625C2.5 15.791 2.566 15.95 2.683 16.067C2.8 16.184 2.959 16.25 3.125 16.25H4.172Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13.75 13.75H15V6.25H5V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Box>
);

const genCircleSx = (state: "done"|"active"|"pending") => ({
  width:32, height:32, borderRadius:"50%",
  display:"flex", alignItems:"center", justifyContent:"center",
  fontWeight:700, fontSize:13, flexShrink:0,
  background:  state==="done"    ? "linear-gradient(135deg,#0168B5,#4BA5EB)" : state==="active" ? "rgba(75,165,235,0.12)" : "transparent",
  border:      state==="done"    ? "none" : state==="active" ? "2px solid #4BA5EB" : "2px solid rgba(255,255,255,0.15)",
  color:       state==="pending" ? "rgba(255,255,255,0.25)" : "#fff",
  boxShadow:   state==="active"  ? "0 0 14px rgba(75,165,235,0.4)" : "none",
});

// ─── Suggestion card renderer ─────────────────────────────────────────────────

interface SuggestionCardProps {
  platform: string;
  item: any;
  loadingIndex: string | null;
  onGenerate: (item: any, key: string) => void;
  platformConfig: any;
  cardKey: string;
  showThumbnail?: boolean;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({
  platform, item, loadingIndex, onGenerate, platformConfig, cardKey, showThumbnail = false,
}) => {
    // Fallback dummy images per platform (used when item has no thumbnail/image_url)
    const FALLBACK_IMAGES: Record<string, string> = {
      socialpost: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=400&h=200&fit=crop",
      blog:       "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=400&h=200&fit=crop",
      course:     "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=200&fit=crop",
      youtube:    "https://images.unsplash.com/photo-1611162616475-46b635cb6868?w=400&h=200&fit=crop",
      linkedin:   "https://images.unsplash.com/photo-1611944212129-29977ae1398c?w=400&h=200&fit=crop",
      twitter:    "https://images.unsplash.com/photo-1611605698335-8b1569810432?w=400&h=200&fit=crop",
      facebook:   "https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?w=400&h=200&fit=crop",
    };

    const thumbnail =
      item?.thumbnail ||
      item?.image_url_small ||
      item?.image_url ||
      FALLBACK_IMAGES[platform] ||
      FALLBACK_IMAGES.socialpost;

    return (
      <Grid item xs={12} sm={4} key={cardKey}>
        <DarkCard sx={{ height: "100%", display: "flex", flexDirection: "column", p: 0, overflow: "hidden" }}>

          {/* Thumbnail */}
          {showThumbnail && thumbnail && (
            <Box sx={{ position: "relative", width: "100%", height: 110, flexShrink: 0, overflow: "hidden" }}>
              <Box
                component="img"
                src={thumbnail}
                alt={item.title}
                onError={(e: any) => {
                  e.currentTarget.src = FALLBACK_IMAGES[platform] || FALLBACK_IMAGES.socialpost;
                }}
                sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }}
              />

              {/* Play icon overlay (video only) */}
              {platform === "youtube" && (
                <Box sx={{
                  position: "absolute", inset: 0,
                  background: "linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.55) 100%)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: "rgba(255,255,255,0.18)", backdropFilter: "blur(4px)",
                    border: "1.5px solid rgba(255,255,255,0.35)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Box component="span" sx={{
                      width: 0, height: 0, ml: "3px",
                      borderTop: "6px solid transparent",
                      borderBottom: "6px solid transparent",
                      borderLeft: "10px solid #fff",
                    }} />
                  </Box>
                </Box>
              )}

              {/* Platform badge */}
              <Box sx={{
                position: "absolute", top: 8, left: 8,
                display: "flex", alignItems: "center", gap: 0.6,
                background: "rgba(0,0,0,0.55)", borderRadius: "6px", px: 1, py: 0.4,
              }}>
                {platform === "youtube" ? (
                  <YouTubeIcon sx={{ fontSize: 14, color: "#FF0000" }} />
                ) : platform === "blog" ? (
                  <ArticleOutlinedIcon sx={{ fontSize: 14, color: "#4BA5EB" }} />
                ) : platform === "course" ? (
                  <ChalkboardIcon sx={{ fontSize: 14, color: "#FFFFFF" } as any} />
                ) : (
                  <CampaignOutlinedIcon sx={{ fontSize: 14, color: "#4BA5EB" }} />
                )}
                <Typography fontSize="10px" fontWeight={600} color="#fff">
                  {platform.toUpperCase()}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Card body — compact */}
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", flex: 1 }}>

            {/* Title — 2 lines max */}
            <Typography
              fontSize="14px"
              fontWeight={600}
              mb={0.8}
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.35,
                color: "#fff",
              }}
            >
              {item.title}
            </Typography>

            {/* Description — 2-3 lines max */}
            {(platform === "blog" || platform === "socialpost" || platform === "youtube" || platform === "course") && item.hook && (
              <Typography
                fontSize="12.5px"
                color="rgba(255,255,255,0.65)"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.55,
                }}
              >
                {item.hook}
              </Typography>
            )}
          </Box>
        </DarkCard>
      </Grid>
    );
  };
// ─── Main Component ───────────────────────────────────────────────────────────

interface Props { userName?: string; userInitials?: string; onDashboard?: () => void; }

const OnboardingCompletePage: React.FC<Props> = ({
  userName = "Rohan", userInitials = "RH", onDashboard,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [getUserDetailsMutation]  = useGetUserDetailsMutation();
  const [getTaskStatus]           = useLazyGetTaskStatusQuery();
  const [generatePost]            = useGeneratePostMutation();

  const user_email = localStorage.getItem("user_email") || getCookie("user_email") || "";

  // ── State ──────────────────────────────────────────────────────────────────
  const [isRecording,        setIsRecording]        = useState(false);
  const [authorityData,      setAuthorityData]      = useState<any>(null);
  const [completedSteps,     setCompletedSteps]     = useState<Set<number>>(new Set());
  const [countdown,          setCountdown]          = useState(5);
  const [redirectStarted,    setRedirectStarted]    = useState(false);
  const [userDetails,        setUserDetails]        = useState<any>();
  const [error,              setError]              = useState<any>(null);
  const [questionText,       setQuestionText]       = useState("");
  const [chatMessages,       setChatMessages]       = useState<any[]>([]);
  const [isChatLoading,      setIsChatLoading]      = useState(false);
  const [lastBotText,        setLastBotText]        = useState<string>("");
  const [loadingIndex,       setLoadingIndex]       = useState<string | null>(null);
  const [videoSuggestions,   setVideoSuggestions]   = useState<any[]>([]);
  const [blogSuggestions,    setBlogSuggestions]    = useState<any[]>([]);
  const [isVideoLoading,     setIsVideoLoading]     = useState(false);
  const [isBlogLoading,      setIsBlogLoading]      = useState(false);
  const [isAuthorityLoading, setIsAuthorityLoading] = useState(false);

  // Completion flags for the async pipelines
  const [authorityComplete,  setAuthorityComplete]  = useState(false);
  const [videoComplete,      setVideoComplete]      = useState(false);
  const [blogComplete,       setBlogComplete]       = useState(false);

  // ── NEW: course details + new-course-suggestion state ─────────────────────
  const [courseDetails,         setCourseDetails]         = useState<any>(null);
  const [courseDetailsLoaded,   setCourseDetailsLoaded]   = useState(false);
  const [newCourseSuggestions,  setNewCourseSuggestions]  = useState<any[]>([]);
  const [isNewCourseLoading,    setIsNewCourseLoading]    = useState(false);
  const [newCourseComplete,     setNewCourseComplete]     = useState(false);

  const [page,               setPage]               = useState(1);
  const [limit]                                     = useState(10);

  const [isDashboardLoading, setIsDashboardLoading] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef   = useRef<BlobPart[]>([]);
  const prevTextRef      = useRef("");
  const hasFetchedBlogRef    = useRef(false);
  const chatScrollRef    = useRef<HTMLDivElement | null>(null);

  const [currentAudioType, setCurrentAudioType] = useState<"intro" | "question" | null>(null);

  const { playText, stop, stopAll, audioRef, isMuted, setIsMuted, isPlaying } = useTTSQueue();

  // ── Derived: does the course have uploaded video content? ─────────────────
  const hasUploadedVideo = Boolean(
    courseDetailsLoaded &&
    Array.isArray(courseDetails?.video) &&
    courseDetails.video.length > 0
  );

  // ── Third pipeline = video (if user has video) OR new course suggestion ───
  const thirdPipelineComplete = courseDetailsLoaded
    ? (hasUploadedVideo ? videoComplete : newCourseComplete)
    : false;

  const allDataLoaded = authorityComplete && blogComplete && thirdPipelineComplete;

  // ── Digital Twin prompt / knowledge mode — set once per onboarding session,
  // read by every generation call below (RTK Query hooks fire on first render,
  // so this must be computed synchronously, not in a useEffect). ──────────────
  const digitalTwinPrompt = useMemo(() => {
    const existing = localStorage.getItem("digital_twin_prompt");
    if (existing) {
      try { return existing; } catch { /* fall through and regenerate */ }
    }
    const text = localStorage.getItem("course_creation_topic") || "";
    const value = { text, timestamp: Date.now() };
    localStorage.setItem("digital_twin_prompt", text);
    return text;
  }, []);

  const digitalTwinKnowledgeMode = useMemo(() => {
    const existing = localStorage.getItem("digital_twin_knowledge_mode");
    if (existing) return existing;
    localStorage.setItem("digital_twin_knowledge_mode", "balanced");
    return "balanced";
  }, []);

  // ── Generic suggestions query (always called) ─────────────────────────────
  const { data: genericData, isLoading: authorityLoading } =
    useGenericEngineSuggestionsTriggerQuery({ user_email: user_email, unsplash_access_key: "", digital_twin_prompt: digitalTwinPrompt, knowledge_mode: digitalTwinKnowledgeMode, });

  // ── Initial cleanup on mount ──────────────────────────────────────────────
  useEffect(() => {
    localStorage.removeItem("authority_engine_cache");

    document.querySelectorAll("audio").forEach(a => {
      try { a.pause(); a.currentTime = 0; } catch {}
    });

    stop();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.onended = null;
    }
  }, []);

  // ── NEW: Fetch course details on mount (decides video vs course path) ─────
  useEffect(() => {
    const courseId = localStorage.getItem("course_id") || "";
    if (!courseId) {
      // No course → no video path possible → go straight to course-suggestion path
      setCourseDetailsLoaded(true);
      return;
    }

    const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
    axios.post(
      `${apiUrl}/api/prompts/get_course_details`,
      { course_id: courseId },
      { headers: { "Content-Type": "application/json", accept: "application/json" } }
    )
      .then(res => {
        setCourseDetails(res?.data?.result || null);
      })
      .catch(err => {
        console.error("[CourseDetails] fetch failed:", err);
        setCourseDetails(null);
      })
      .finally(() => {
        setCourseDetailsLoaded(true);
      });
  }, []);

  useEffect(() => {
    if (!genericData?.task_id) return;
    setIsAuthorityLoading(true);
    pollTaskStatus(genericData.task_id)
      .then(result => {
        setAuthorityData(result?.data?.suggested_content || []);
      })
      .catch(err => console.error("Generic suggestion polling error:", err))
      .finally(() => {
        setIsAuthorityLoading(false);
        setAuthorityComplete(true);
      });
  }, [genericData?.task_id]);

  // ── Video suggestions: only fire when user has uploaded video ─────────────
  const { data: videoData, isLoading: videoQueryLoading } =
    useGetVideoSuggestionsTriggerQuery(
      { email: user_email, digital_twin_prompt: digitalTwinPrompt, knowledge_mode: digitalTwinKnowledgeMode,},
      { skip: !courseDetailsLoaded || !hasUploadedVideo },
    );

  // ── Normalizers — match what AuthorityEngine expects ──────────────────────
  const normalizeBlogItem = (item: any) => ({
    type: "blog",
    title: item.title || item.name,
    hook: item.description || item.hook || "",
    platform: Array.isArray(item.platform)
      ? item.platform.map((p: string) => p.toLowerCase())
      : Array.isArray(item.platforms)
        ? item.platforms.map((p: string) => p.toLowerCase())
        : [],
    image_url: item.image_url || item.thumbnail || "https://picsum.photos/400/200",
    source_reference: item.source_reference || "",
    key_points: item.key_points || item.tags || [],
    content_angle: item.content_angle,
    estimated_engagement: item.estimated_engagement || item.engagement_potential,
  });

  const normalizeVideoItem = (item: any) => ({
    type: "video",
    title: item.title,
    hook: item.hook,
    platform: Array.isArray(item.platform)
      ? item.platform.map((p: string) => p.toLowerCase())
      : Array.isArray(item.platforms)
        ? item.platforms.map((p: string) => p.toLowerCase())
        : [],
    image_url: item.image_url || item.thumbnail,
    source_reference: item.source_reference || item.media_url || "",
    key_points: item.key_points || [],
    content_angle: item.content_angle,
    estimated_engagement: item.estimated_engagement,
  });

  // Cache version must match AuthorityEngine's CACHE_VERSION constant.
  // Bump this in BOTH files whenever the cached data shape changes.
  const CACHE_VERSION = 2;

  useEffect(() => {
    const hasAny =
      authorityData ||
      blogSuggestions?.length ||
      videoSuggestions?.length ||
      newCourseSuggestions?.length;

    if (!hasAny) return;

    // Normalize blog/video into the shape AuthorityEngine's render expects.
    const normalizedBlogs = Array.isArray(blogSuggestions)
      ? blogSuggestions.map(normalizeBlogItem)
      : [];
    const normalizedVideos = Array.isArray(videoSuggestions)
      ? videoSuggestions.map(normalizeVideoItem)
      : [];
    const normalizedCourses = Array.isArray(newCourseSuggestions)
      ? newCourseSuggestions
      : [];

    // Write under the EXACT keys AuthorityEngine expects.
    // - blogTriggerSuggestions (not "blogSuggestions")
    // - randomCourseSuggestion (course card reads from here)
    // - __version stamp (AuthorityEngine's version check requires it)
    const cachePayload = {
      __version: CACHE_VERSION,
      authorityData,
      blogTriggerSuggestions: normalizedBlogs,
      videoSuggestions:       normalizedVideos,
      randomCourseSuggestion: normalizedCourses,
      newCourseSuggestions:   normalizedCourses,  // keep both so either component can read
    };

    localStorage.setItem(
      "authority_engine_cache",
      JSON.stringify(cachePayload)
    );
  }, [authorityData, blogSuggestions, videoSuggestions, newCourseSuggestions]);

  // ── Video polling effect (handles both "has video" and "no video" cases) ──
  useEffect(() => {
    // If we already decided this is the course-suggestion path, mark video
    // complete immediately so allDataLoaded can settle.
    if (courseDetailsLoaded && !hasUploadedVideo) {
      setVideoComplete(true);
      return;
    }

    if (!videoData?.task_id) return;
    setIsVideoLoading(true);
    pollTaskStatus(videoData.task_id)
      .then(result => {
        setVideoSuggestions(result?.data?.video_suggestions || []);
      })
      .catch(err => console.error("Video suggestion polling error:", err))
      .finally(() => {
        setIsVideoLoading(false);
        setVideoComplete(true);
      });
  }, [videoData?.task_id, courseDetailsLoaded, hasUploadedVideo]);

  // ── Blog suggestions query (always called) ───────────────────────────────
  // const { data: blogData, isLoading: blogQueryLoading } =
  //   useGetBlogSuggestionsTriggerQuery({ user_email: user_email });



  useEffect(() => {
    if (!user_email) return;
    if (hasFetchedBlogRef.current) return;   // guard: only fire once
    hasFetchedBlogRef.current = true;
    fetchNewBlogSuggestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_email]);

  const fetchNewBlogSuggestion = async () => {
    if (!user_email) return;
    try {
      setIsBlogLoading(true);
      const apiUrl   = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const courseId = localStorage.getItem("course_id") || "";

      const triggerRes = await axios.post(
        `${apiUrl}/api/prompts/process_create_new_blog_suggestions_trigger`,
        { user_email, course_id: courseId, digital_twin_prompt: digitalTwinPrompt, knowledge_mode: digitalTwinKnowledgeMode, },
        { headers: { "Content-Type": "application/json", accept: "application/json" } }
      );
      const taskId = triggerRes.data?.task_id;
      if (!taskId) {
        console.warn("[BlogSuggestion] no task_id returned:", triggerRes.data);
        return;
      }

      const result = await pollTaskStatus(taskId);
      console.log("[OnboardingComplete] blog poll result:", result);

      // ── Coerce to array regardless of response shape ─────────────────────
      let list: any[] = [];
      if      (Array.isArray(result?.data?.content_suggestions)) list = result.data.content_suggestions;
      else if (Array.isArray(result?.data?.blog_suggestions))    list = result.data.blog_suggestions;
      else if (Array.isArray(result?.data))                      list = result.data;
      else if (Array.isArray(result?.content_suggestions))       list = result.content_suggestions;
      else if (Array.isArray(result?.blog_suggestions))          list = result.blog_suggestions;
      else if (Array.isArray(result))                            list = result;

      setBlogSuggestions(list);
    } catch (err) {
      console.error("[BlogSuggestion] fetch failed:", err);
    } finally {
      setIsBlogLoading(false);
      setBlogComplete(true);
    }
  };

  // useEffect(() => {
  //   if (!blogData?.task_id) return;
  //   setIsBlogLoading(true);
  //   pollTaskStatus(blogData.task_id)
  //     .then(result => {
  //       console.log("[OnboardingComplete] blog poll result:", result);

  //       let list: any[] = [];
  //       if (Array.isArray(result?.data?.content_suggestions)) {
  //         list = result.data.content_suggestions;
  //       } else if (Array.isArray(result?.data?.blog_suggestions)) {
  //         list = result.data.blog_suggestions;
  //       } else if (Array.isArray(result?.data)) {
  //         list = result.data;
  //       } else if (Array.isArray(result?.content_suggestions)) {
  //         list = result.content_suggestions;
  //       } else if (Array.isArray(result?.blog_suggestions)) {
  //         list = result.blog_suggestions;
  //       } else if (Array.isArray(result)) {
  //         list = result;
  //       }

  //       setBlogSuggestions(list);
  //     })
  //     .catch(err => console.error("Blog suggestion polling error:", err))
  //     .finally(() => {
  //       setIsBlogLoading(false);
  //       setBlogComplete(true);
  //     });
  // }, [blogData?.task_id]);

  // ── NEW: New course suggestion fetcher (only when no uploaded video) ──────
  useEffect(() => {
    if (!courseDetailsLoaded) return;
    if (hasUploadedVideo)      return;
    if (!user_email)           return;
    fetchNewCourseSuggestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseDetailsLoaded, hasUploadedVideo, user_email]);
  
  const fetchNewCourseSuggestion = async () => {
    if (!user_email) return;
    try {
      setIsNewCourseLoading(true);
      const apiUrl   = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const courseId = localStorage.getItem("course_id") || "";
      const triggerRes = await axios.post(
        `${apiUrl}/api/prompts/process_create_courses_topics_suggestions_trigger`,
        {},
        {
          params: { user_email: user_email, course_id: courseId, result_count: 1, 
            digital_twin_prompt: JSON.stringify(digitalTwinPrompt), knowledge_mode: digitalTwinKnowledgeMode},
          headers: { accept: "application/json" },
        }

      );
      const taskId = triggerRes.data?.task_id;
      if (!taskId) {
        console.warn("[NewCourseSuggestion] no task_id:", triggerRes.data);
        return;
      }
      const result = await pollTaskStatus(taskId);

      let rawList: any[] = [];
      if      (Array.isArray(result?.data))                      rawList = result.data;
      else if (Array.isArray(result?.data?.courses))             rawList = result.data.courses;
      else if (Array.isArray(result?.data?.course_suggestions))  rawList = result.data.course_suggestions;
      else if (Array.isArray(result?.courses))                   rawList = result.courses;
      else if (Array.isArray(result?.course_suggestions))        rawList = result.course_suggestions;
      else if (Array.isArray(result))                            rawList = result;

      const normalized = rawList.map((item: any) => ({
        type: "course",

        // Display fields the card reads
        title: item.title || item.name,
        hook:  item.description || item.hook || "",   // ← API uses "description"

        // Image
        image_url:       item.image_url,
        image_url_small: item.image_url_small || item.thumbnail || item.image_url,

        // Platform (empty for courses, but kept for the shared list shape)
        platform: item.platforms || item.platform || [],

        // Fields AuthorityEngine's renderRecommendationCard / handleSaveCourseAndPush expect
        source_reference:     item.description || item.source_reference || "",
        key_points:           item.learning_outcomes || item.key_points || [],
        content_angle:        item.target_audience || item.category || item.content_angle || "",
        estimated_engagement: item.engagement_potential || item.estimated_engagement || "",

        // FULL payload — needed for save-course / push-courses calls
        parent_course_id:   item.parent_course_id,
        course_id:          item.parent_course_id,    // alias kept for back-compat
        course_number:      item.course_number,
        name:               item.name,
        description:        item.description,
        target_audience:    item.target_audience,
        duration:           item.duration,
        pricing:            item.pricing,
        learning_outcomes:  item.learning_outcomes,
        curriculum:         item.curriculum,
        category:           item.category,
        difficulty_level:   item.difficulty_level,
        estimated_duration: item.estimated_duration,
        suggested_modules:  item.suggested_modules,
        prerequisites:      item.prerequisites,
        tags:               item.tags,
      }));


      setNewCourseSuggestions(normalized);

      // Persist the first raw course for AuthorityEngine's handleSaveCourseAndPush
      // fallback — it reads this from localStorage if the clicked item is missing
      // parent_course_id.
      try {
        if (rawList.length > 0) {
          localStorage.setItem("course_suggestion_selected", JSON.stringify(rawList[0]));
        }
      } catch (e) {
        console.warn("Could not persist course_suggestion_selected:", e);
      }

    } catch (err) {
      console.error("[NewCourseSuggestion] failed:", err);
    } finally {
      setIsNewCourseLoading(false);
      setNewCourseComplete(true);
    }
  };

  // ── TTS mute sync ──────────────────────────────────────────────────────────
  useEffect(() => {
    (window as any).isMutedGlobal = isMuted;
    if (audioRef.current) audioRef.current.muted = isMuted;
  }, [isMuted]);

  useEffect(() => {
    stopAll();
    document.querySelectorAll("audio").forEach(a => {
      try { a.pause(); a.currentTime = 0; a.src = ""; } catch {}
    });
    return () => {
      stopAll();
    };
  }, []);

  const location_audio = useLocation();

  useEffect(() => {
    stopAllAudio();
    return () => {
      stopAllAudio();
      document.querySelectorAll("audio").forEach(a => {
        try { a.pause(); a.currentTime = 0; } catch {}
      });
    };
  }, [location_audio.pathname]);

  const stopAllAudio = () => {
    stopAll();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.onended = null;
    }
  };

  // ── Poll helper ───────────────────────────────────────────────────────────
  const pollTaskStatus = async (taskId: string) => {
    for (let i = 0; i < 60; i++) {
      try {
        const res = await getTaskStatus(taskId).unwrap();
        if (res.state === "done")   return res.result;
        if (res.state === "failed") throw new Error("Task failed");
      } catch (err) { console.error("Polling error:", err); }
      await new Promise(r => setTimeout(r, 5000));
    }
    throw new Error("Polling timeout");
  };

  // Step 1, 2, 3 progress on timers
  useEffect(() => {
    const timers = GENERATION_STEPS
      .filter(s => s.delay > 0)
      .map(step =>
        setTimeout(() => {
          setCompletedSteps(prev => {
            const next = new Set(prev);
            next.add(step.id);
            return next;
          });
        }, step.delay)
      );
    return () => timers.forEach(clearTimeout);
  }, []);

  // Step 4 completes when ALL three pipelines have finished
  useEffect(() => {
    if (!allDataLoaded) return;
    setCompletedSteps(prev => {
      const next = new Set(prev);
      next.add(4);
      return next;
    });
  }, [allDataLoaded]);

  // ── Recording ─────────────────────────────────────────────────────────────
  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    const stream       = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
    audioChunksRef.current = [];
    mediaRecorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
    mediaRecorder.onstop = sendAudioToBackend;
    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
    setIsRecording(true);
  };
  const stopRecording = () => { mediaRecorderRef.current?.stop(); setIsRecording(false); };

  const sendAudioToBackend = async () => {
    const audioBlob = new Blob(audioChunksRef.current, {
        type: "audio/webm",
    });

    await saveVoiceSample(audioBlob);

    const formData = new FormData();
    formData.append("audio", audioBlob, "speech.webm");

    try {
      const res  = await fetch(`${process.env.REACT_APP_SPEECH_TO_TEXT_SERVICE}/speech_to_text`, { method:"POST", body:formData });
      const data = await res.json();
      setQuestionText(prev => `${prev} ${data.text}`.trim());
    } catch (err) { console.error(err); }
  };

  // ── Chat ──────────────────────────────────────────────────────────────────
  const stripHtml = (html: string) => { const d = document.createElement("div"); d.innerHTML = html; return d.textContent || d.innerText || ""; };

  const formatAIResponse = (text: string) => {
    if (!text) return "";
    return text
      .replace(/\*{2,}/g, "").replace(/#{1,}/g, "").replace(/^\* (.+)$/gm, "• $1")
      .replace(/(?:^|\n)(• .*(?:\n• .*)*)/g, match => `<ul>${match.trim().split("\n").map(l => `<li>${l.replace(/^• /,"")}</li>`).join("")}</ul>`)
      .replace(/^(.+):$/gm, "<h3>$1</h3>")
      .replace(/^(?!<h3>|<ul>|<li>|<\/ul>)(.+)$/gm, "<p>$1</p>")
      .replace(/<p>\s*<\/p>/g, "");
  };

  const getChatBotResponse = async (query: string) => {
    if (!query.trim()) return;
    setChatMessages(prev => [...prev, { role:"user", text:query }]);
    setQuestionText(""); setIsChatLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const res    = await axios.post(`${apiUrl}/api/prompts/chat_answer`, { courseid:"6a14a6aa195a2644a8352677", query, chapter:"" });
      let answer   = (res?.data?.response || "No response").replace(/\*{2,}/g,"").replace(/#{1,}/g,"").replace(/^\* (.+)$/gm,"• $1");
      setChatMessages(prev => [...prev, { role:"bot", text: formatAIResponse(answer) }]);
      setLastBotText(stripHtml(answer));
    } catch (err) { console.error(err); }
    finally { setIsChatLoading(false); }
  };

  useEffect(() => {
    if (!lastBotText || prevTextRef.current === lastBotText) return;
    prevTextRef.current = lastBotText; stop(); setCurrentAudioType(null); playText(lastBotText);
  }, [lastBotText]);

  // ── Audio intro ───────────────────────────────────────────────────────────
  const secondAudio = "https://texttospeachservice.ocesha.ai/media/838dd3a5-2afe-4a14-9f2d-d482f16fe958.wav";

  useEffect(() => { if (userDetails?.first_name) fetchAudio(); }, [userDetails?.first_name]);

  const fetchAudio = async () => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_TEXT_TO_SPEECH_SERVICE}/speak?text=${encodeURIComponent(`Hi ${userDetails?.first_name}`)}`);
      if (res?.data?.audio_path) playAudio(res.data.audio_path);
    } catch (err) { console.log(err); }
  };

  const playAudio = (url: string) => {
    const audio = new Audio(url);
    audioRef.current = audio; audio.muted = isMuted;
    setCurrentAudioType("intro");
    audio.play().catch(() => {
      const retry = () => { audio.play().catch(()=>{}); document.removeEventListener("click",retry); document.removeEventListener("keydown",retry); };
      document.addEventListener("click",   retry, { once:true });
      document.addEventListener("keydown", retry, { once:true });
    });
    audio.onended = () => {
      const next = new Audio(secondAudio); audioRef.current = next; next.muted = isMuted;
      next.play().catch(()=>{}); next.onended = ()=>{setCurrentAudioType(null);};
    };
  };

  const toggleMute = () => setIsMuted(prev => {
    const next = !prev;
    if (audioRef.current) {
      audioRef.current.muted = next;
      if (next) audioRef.current.pause();
      else audioRef.current.play().catch(()=>{});
    }
    (window as any).isMutedGlobal = next;
    return next;
  });

  // ── Generate post ─────────────────────────────────────────────────────────
  const handleGenerate = async (item: any, index: string) => {
    try {
      setLoadingIndex(index); setPage(1);
      const platform = item.type === "video"
        ? (Array.isArray(item.platform) ? item.platform.join(" | ") : item.platform || "")
        : item.platform;
      const res: any = await generatePost({ email:user_email, 
        digital_twin_prompt: digitalTwinPrompt, knowledge_mode: digitalTwinKnowledgeMode,
        suggestion:{ type:item.type, title:item.title, hook:item.hook, source_reference:item.source_reference, platform, estimated_engagement:item.estimated_engagement, content_angle:item.content_angle, key_points:item.key_points, image_url:item.image_url, image_url_small:item.image_url_small } }).unwrap();
      if (res?.posts) { toast.success("Post generated"); }
    } catch { toast.error("Failed to generate"); }
    finally { setLoadingIndex(null); }
  };

  // ── User details ──────────────────────────────────────────────────────────
  const fetchData = async () => {
    try {
      const response = await getUserDetailsMutation({ email: user_email || getCookie("user_email") || "", filter:"", start_date:"", end_date:"" });
      if ("data" in response) setUserDetails(response.data.data);
    } catch (e) { console.log(e); }
  };
  useEffect(() => { fetchData(); }, [user_email]);

  // ── Countdown ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const allDone = GENERATION_STEPS.every(s => completedSteps.has(s.id));
    if (!allDone) return;
    setRedirectStarted(true);
    const iv = setInterval(() => setCountdown(c => { if (c<=1){ clearInterval(iv); onDashboard?.(); return 0; } return c-1; }), 1000);
    return () => clearInterval(iv);
  }, [completedSteps, onDashboard]);

    // Auto-scroll AI chat to the latest message
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    // Use rAF so it scrolls AFTER the new message is painted (esp. for bot HTML)
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
  }, [chatMessages, isChatLoading]);

  const stepState = (s: GenerationStep): "done"|"active"|"pending" => {
    if (completedSteps.has(s.id)) return "done";
    return (s.id === 1 || completedSteps.has(s.id - 1)) ? "active" : "pending";
  };

  const handleItemClick = (itemName: any) => {
    localStorage.setItem("activeItem", itemName);
    if (itemName === "logout") { 
      dispatch(logout()); localStorage.clear(); removeCookie("user_email"); toast.success("Logout success"); window.location.href = process.env.REACT_APP_LOGOUT_DOMAIN||''; //"https://ocesha.ai"; 
    }
    else if (itemName === "course") navigate("/codegenie/generated-courses");
    else navigate("/codegenie/generated-blogs");
  };

  const platformConfig: any = {
    linkedin: { icon: <LinkedInIcon  sx={{ fontSize:16, color:"#0A66C2" }} /> },
    twitter:  { icon: <TwitterIcon   sx={{ fontSize:16, color:"#1DA1F2" }} /> },
    facebook: { icon: <FacebookIcon  sx={{ fontSize:16, color:"#1877F2" }} /> },
    youtube:  { icon: <YouTubeIcon   sx={{ fontSize:16, color:"#FF0000" }} /> },
    both:     { icon: <ChalkboardIcon sx={{ fontSize:16, color:"#FFFFFF" } as any} /> },
    course:   { icon: <ChalkboardIcon sx={{ fontSize:16, color:"#FFFFFF" } as any} /> },
    other:    { icon: <DescriptionOutlinedIcon sx={{ fontSize:16 }} /> },
  };

  const trainDigitalTwinVoice = async () => {
    try {
      const samples = getVoiceSamples();

      if (!samples.length) {
        return true;
      }

      const voiceName =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `voice_${Date.now()}`;

      const formData = new FormData();

      formData.append("voice_name", voiceName);

      for (let i = 0; i < samples.length; i++) {
        const file = await base64ToFile(
          samples[i],
          `voice_${i}.webm`
        );

        formData.append("files", file);
      }

      const trainResponse = await axios.post(
        "https://tts.ocesha.ai/train_voice",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (
        trainResponse?.data?.status !== "voice_trained"
      ) {
        throw new Error("Voice training failed");
      }

      const trainedVoiceName =
        trainResponse?.data?.voice_name || voiceName;

      const apiUrl: string = process.env.REACT_APP_SERVER_ENDPOINT || '';

      const updateResponse = await axios.post(
        `${apiUrl}/api/auth/update_digitial_twin_voice`,
        null,
        {
          params: {
            email: user_email,
            voice_name: trainedVoiceName,
          },
        }
      );

      localStorage.setItem(
        "digital_twin_voice",
        trainedVoiceName
      );

      clearVoiceSamples();

      console.log(
        "Voice trained successfully:",
        trainedVoiceName
      );

      return true;
    } catch (err) {
      console.error("Voice training failed", err);
      return false;
    }
  };

  useEffect(() => {
        console.log({
            isPlaying,
            isMuted,
            currentAudioType,
        });
    }, [isPlaying, isMuted, currentAudioType]);

    const isSpeakerAnimating = (currentAudioType !== null || isPlaying) && !isMuted;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes confettiFall { 0%{opacity:1;transform:translateY(-10px) rotate(0deg)} 100%{opacity:0;transform:translateY(60px) rotate(360deg)} }
        @keyframes spinSlow     { to{transform:rotate(360deg)} }
        @keyframes pulseDot     { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes fadeSlideUp  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      <div style={{ overflowX:"hidden" }}>
        <div className="row">
          <div className="col-sm-12 col-md-12 col-lg-12">
            <div style={{ height:1, background:"rgba(255,255,255,0.08)", margin:"8px 0" }} />

            <div className="overlay-container" style={{ paddingRight:"10%", paddingLeft:"10%" }}>
              <div style={{ width:"98%", marginBottom:"10%" }}>
                <div className="row gx-2">

                  {/* ════════ LEFT PANEL ════════ */}
                  <div className="col-sm-12 col-md-4 col-lg-4 py-4" style={{ paddingLeft:"1.5rem" }}>
                    
                    <Box
                        sx={{
                            position: "relative",
                            display: "block",
                            width: "100%",
                            overflow: "visible",
                        }}
                    >
                        {/* Rohan image — no bump animation */}
                        <Box
                            component="img"
                            src={RohanImg}
                            alt="Rohan Hall"
                            sx={{
                                position: "relative",
                                zIndex: 1,
                                width: "100%",
                                display: "block",
                                objectFit: "cover",
                            }}
                        />

                        {/* Waveform bars — overlaid on the bottom of the image */}
                        <Box
                            sx={{
                                display: isSpeakerAnimating ? "flex" : "none",
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: "16px",          // distance from the bottom edge of the image
                                zIndex: 2,
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "3px",
                                height: "48px",
                                px: 2,
                                pointerEvents: "none",   // so it doesn't block clicks on the image
                                background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.35) 100%)",
                            }}
                        >
                            {[
                                4, 10, 18, 8, 22, 14, 26, 6, 16, 30, 12, 20, 8, 24, 10, 18, 4, 14, 22, 8,
                                16, 28, 10, 20, 6, 24, 12, 18, 8, 14, 26, 10, 16, 22, 6, 20, 12, 8, 24, 14,
                            ].map((peak, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        width: "2.5px",
                                        minWidth: "2.5px",
                                        borderRadius: "2px",
                                        background: "#3B82F6",
                                        animation: `waveStrip${i} 1.1s ease-in-out infinite`,
                                        animationDelay: `${(i % 10) * 0.08}s`,
                                        [`@keyframes waveStrip${i}`]: {
                                            "0%, 100%": { height: "6px" },
                                            "50%": { height: `${peak}px` },
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                    <Paper sx={{ background:"linear-gradient(180deg,rgba(11,12,20,1),rgba(10,14,28,1))", borderTopLeftRadius:0, borderTopRightRadius:0, borderBottomLeftRadius:"16px", borderBottomRightRadius:"16px", border:"1px solid rgba(255,255,255,0.08)", p:3, mb:2 }}>
                        <Box position="relative"
                        sx={{
                            maxHeight: "300px", // fixed height
                            overflowY: "auto",
                            pr: 1, // space for scrollbar

                            // Thin scrollbar
                            "&::-webkit-scrollbar": {
                              width: "4px",
                            },
                            "&::-webkit-scrollbar-track": {
                              background: "transparent",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              background: "rgba(255,255,255,0.18)",
                              borderRadius: "10px",
                            },
                            "&::-webkit-scrollbar-thumb:hover": {
                              background: "rgba(255,255,255,0.3)",
                            },

                            // Firefox
                            scrollbarWidth: "thin",
                            scrollbarColor: "rgba(255,255,255,0.18) transparent",
                          }}>
                          <Typography sx={{ fontSize:"11px", letterSpacing:"0.08em", color:"#4BA5EB", mb:1, display:"flex", alignItems:"center", gap:1 }}>
                            <AutoAwesomeIcon sx={{ fontSize:16 }} /> MEET YOUR AI CONCIERGE
                          </Typography>
                          <Typography sx={{ color:"#fff", fontSize:"22px", fontWeight:600, mb:1.5 }}>Hi {userDetails?.first_name},</Typography>
                          <Typography sx={{ color:"#A0AEC0", fontSize:"14px", lineHeight:"22px" }}>
                            You're all set!<br /><br />
                            OceSha now understands your expertise, uploaded knowledge, and preferences.
                          </Typography>
                          <br />
                          <Typography sx={{ color:"#A0AEC0", fontSize:"14px", lineHeight:"22px" }}>
                            We're ready to help you:
                          </Typography>
                          <Box
                              component="ul"
                              sx={{
                                  color: "#A0AEC0",
                                  fontSize: "14px",
                                  lineHeight: "22px",
                                  pl: 3,
                                  mt: 1,
                                  mb: 0,
                                  "& li": { mb: 0.5 },
                              }}
                              >
                              <li>Create courses</li>
                              <li>Let your AI teach</li>
                              <li>Create marketing content</li>
                              <li>Grow your course business</li>
                            </Box>
                            <br />

                            <Typography sx={{ color:"#A0AEC0", fontSize:"14px", lineHeight:"22px" }}>
                              Let's get started.
                            </Typography>
                        </Box>
                    </Paper>

                    {/* Ask AI */}
                    <Paper sx={{ p:2, background:"#2b2b32", borderRadius:"8px" }}>
                      <Box
                          ref={chatScrollRef}                                 
                          sx={{ maxHeight:200, overflowY:"auto", mb:1, scrollBehavior:"smooth" }}
                        >
                        {chatMessages.map((msg, i) => (
                          <Box key={i} sx={{ textAlign:msg.role==="user"?"right":"left", mb:1 }}>
                            <Typography sx={{ display:"inline-block", px:1.5, py:0.8, borderRadius:2, background:msg.role==="user"?"#4BA5EB":"#000000", color:"#fff", fontSize:"14px" }}>
                              {msg.role==="bot" ? <Box dangerouslySetInnerHTML={{ __html:msg.text }} /> : msg.text}
                            </Typography>
                          </Box>
                        ))}
                        {isChatLoading && <Typography sx={{ color:"#aaa", fontSize:12 }}>Thinking...</Typography>}
                      </Box>
                      <textarea value={questionText} onChange={e=>setQuestionText(e.target.value)} placeholder="Ask AI anything..." rows={2}
                        style={{ width:"100%", background:"#2b2b32", color:"#fff", border:"none", outline:"none", resize:"none", fontFamily:"inherit", fontSize:13 }} />
                      <Stack direction="row" justifyContent="flex-end" spacing={1} mt={1}>
                        <IconButton onClick={toggleMute} sx={{ background:isMuted?"linear-gradient(135deg,#667eea,#764ba2)":"linear-gradient(135deg,#4BA5EB,#1E7BC0)", borderRadius:"12px", width:45, height:45, border:"1px solid rgba(255,255,255,0.2)", "&:hover":{transform:"scale(1.1)"} }}>
                          {isMuted ? <VolumeOffIcon sx={{ color:"#fff", fontSize:"22px" }} /> : <VolumeUpIcon sx={{ color:"#fff", fontSize:"22px" }} />}
                        </IconButton>
                        <IconButton onClick={()=>isRecording?stopRecording():startRecording()} className={isRecording?"recording-mic-btn":"mic-btn"}>
                          <MicNoneOutlinedIcon sx={{ color:"#fff", fontSize:"20px" }} />
                        </IconButton>
                        <IconButton onClick={()=>getChatBotResponse(questionText)}>
                          <SendIcon sx={{ color:"#fff" }} />
                        </IconButton>
                      </Stack>
                    </Paper>
                  </div>

                  {/* ════════ RIGHT PANEL ════════ */}
                  <div className="col-md-8">
                    <Box sx={{ px:{xs:2,md:4}, pl:{md:2}, py:2, display:"flex", flexDirection:"column", gap:3 }}>

                      {/* Hero */}
                      <Box sx={{ textAlign:"center", animation:"fadeSlideUp 0.5s ease", width:"100%" }}>
                        <Confetti />
                        <Box sx={{ fontSize:52, mb:1 }}></Box>
                        <Typography sx={{ fontSize:{xs:28,md:38}, fontWeight:800, color:"#fff", lineHeight:1.25, mb:2, textAlign:"center" }}>
                          You're all set,{" "}
                          <span style={{ background:"linear-gradient(90deg,#4BA5EB,#60d5f5)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
                            {userDetails?.first_name}!
                          </span>
                        </Typography>
                        <Typography sx={{ fontSize:{xs:15,md:17}, color:"#9a9cb0", lineHeight:1.7, textAlign:"center" }}>
                          I've captured your expertise and preferences.<br />
                          Your first content is being generated right now.
                        </Typography>
                      </Box>

                      {/* Generation progress */}
                      <Box sx={{ background:"rgba(255,255,255,0.025)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:"14px", p:"20px 24px" }}>
                        <Box sx={{ display:"flex", alignItems:"center", gap:2, mb:3 }}>
                          <Box sx={{ width:44, height:44, borderRadius:"12px", background:"rgba(75,165,235,0.1)", border:"1px solid rgba(75,165,235,0.25)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                            <SmartToyOutlinedIcon sx={{ fontSize:22, color:"#4BA5EB" }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize:14, fontWeight:700, color:"#e8eaf0" }}>
                              {allDataLoaded
                                ? "Your first content is ready!"
                                : "Creating your first content…"}
                            </Typography>
                            <Typography sx={{ fontSize:12, color:"#7a7d96", mt:0.3 }}>
                              {allDataLoaded
                                ? "All set — you can head to your dashboard."
                                : "This usually takes less than 60 seconds."}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display:"flex", alignItems:"flex-start", justifyContent:"center" }}>
                          {GENERATION_STEPS.map((step, idx) => {
                            const state  = stepState(step);
                            const isLast = idx === GENERATION_STEPS.length - 1;
                            return (
                              <React.Fragment key={step.id}>
                                <Box sx={{ display:"flex", flexDirection:"column", alignItems:"center", maxWidth:110 }}>
                                  <Box sx={genCircleSx(state)}>
                                    {state==="done"               ? <CheckIcon sx={{ fontSize:16 }} />
                                      : state==="active"&&isLast  ? <AutoAwesomeIcon sx={{ fontSize:16, color:"#4BA5EB", animation:"spinSlow 2s linear infinite" }} />
                                      : state==="active"          ? <Box sx={{ width:8, height:8, borderRadius:"50%", background:"#4BA5EB", animation:"pulseDot 1s ease-in-out infinite" }} />
                                      : null}
                                  </Box>
                                  <Typography sx={{ fontSize:11, color:state==="active"?"#4BA5EB":state==="done"?"#e8eaf0":"#5a5d70", fontWeight:state==="active"?700:500, mt:1, textAlign:"center", lineHeight:1.4 }}>
                                    {step.label}
                                  </Typography>
                                </Box>
                                {!isLast && <Box sx={{ flex:1, height:2, background:completedSteps.has(step.id)?"linear-gradient(90deg,#0168B5,#4BA5EB)":"rgba(255,255,255,0.08)", maxWidth:110, borderRadius:99, mt:2 }} />}
                              </React.Fragment>
                            );
                          })}
                        </Box>
                      </Box>

                      {/* ── Suggestions — 3 columns, 1 row ── */}
                      <Box>
                        <Typography sx={{ display:"flex", alignItems:"center", gap:1, mb:2, fontSize:15, fontWeight:700, color:"#e8eaf0" }}>
                          <AutoAwesomeIcon sx={{ fontSize:16, color:"#4BA5EB" }} />
                          Here's what's coming your way:
                        </Typography>

                        {error ? (
                          <Typography color="error">Failed to load suggestions.</Typography>
                        ) : (
                          <Grid container spacing={2}>

                            {/* Card 1 — first authority suggestion */}
                            {isAuthorityLoading ? (
                              <Grid item xs={12} sm={4}><SuggestedCardSkeleton /></Grid>
                            ) : authorityData &&
                                typeof authorityData === "object" &&
                                Object.keys(authorityData).length > 0 && (() => {
                              const platform = "socialpost";
                              const item     = authorityData[0];
                              const cardKey  = `auth-0-${platform}`;
                              return (
                                <SuggestionCard key={cardKey} cardKey={cardKey}
                                  platform={platform} item={item}
                                  loadingIndex={loadingIndex} onGenerate={handleGenerate}
                                  platformConfig="authority"
                                  showThumbnail={true}
                                />
                              );
                            })()}

                            {/* Card 2 — first blog suggestion */}
                            {isBlogLoading ? (
                              <Grid item xs={12} sm={4}><SuggestedCardSkeleton /></Grid>
                            ) : Array.isArray(blogSuggestions) && blogSuggestions.length > 0 && (() => {
                              const item     = blogSuggestions[0];
                              const platform = "blog";
                              const cardKey  = `auth-1-${platform}`;
                              return (
                                <SuggestionCard key={cardKey} cardKey={cardKey}
                                  platform={platform} item={item}
                                  loadingIndex={loadingIndex} onGenerate={handleGenerate}
                                  platformConfig="blog"
                                  showThumbnail={true}
                                />
                              );
                            })()}

                            {/* Card 3 — video (if user has video) OR new course suggestion */}
                            {!courseDetailsLoaded ? (
                              <Grid item xs={12} sm={4}><SuggestedCardSkeleton /></Grid>
                            ) : hasUploadedVideo ? (
                              isVideoLoading ? (
                                <Grid item xs={12} sm={4}><SuggestedCardSkeleton /></Grid>
                              ) : Array.isArray(videoSuggestions) && videoSuggestions.length > 0 && (() => {
                                const item     = videoSuggestions[0];
                                const platform = Array.isArray(item?.platform) ? item.platform[0] : item?.platform || "youtube";
                                return (
                                  <SuggestionCard key="video-0" cardKey="video-0"
                                    platform={platform} item={item}
                                    loadingIndex={loadingIndex} onGenerate={handleGenerate}
                                    platformConfig="video"
                                    showThumbnail={true}
                                  />
                                );
                              })()
                            ) : (
                              isNewCourseLoading ? (
                                <Grid item xs={12} sm={4}><SuggestedCardSkeleton /></Grid>
                              ) : Array.isArray(newCourseSuggestions) && newCourseSuggestions.length > 0 && (() => {
                                const item = newCourseSuggestions[0];
                                return (
                                  <SuggestionCard key="course-0" cardKey="course-0"
                                    platform="course" item={item}
                                    loadingIndex={loadingIndex} onGenerate={handleGenerate}
                                    platformConfig="course"
                                    showThumbnail={true}
                                  />
                                );
                              })()
                            )}
                          </Grid>
                        )}
                      </Box>

                      {/* CTA banner */}
                      <Box sx={{ display:"flex", alignItems:"center", gap:2, flexWrap:"wrap", background:"rgba(255,255,255,0.025)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:"14px", p:"18px 22px" }}>
                        <Box sx={{ width:44, height:44, borderRadius:"12px", background:"rgba(75,165,235,0.08)", border:"1px solid rgba(75,165,235,0.2)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <RocketLaunchOutlinedIcon sx={{ fontSize:22, color:"#4BA5EB" }} />
                        </Box>
                        <Box sx={{ flex:1, minWidth:200 }}>
                          <Typography sx={{ fontSize:14, fontWeight:700, color:"#e8eaf0", mb:0.5 }}>
                            {allDataLoaded
                              ? "Your content is ready — let's go!"
                              : "You're just one step away from seeing your content!"}
                          </Typography>
                          <Typography sx={{ fontSize:13, color:"#7a7d96" }}>
                            {allDataLoaded
                              ? "Head to your dashboard to explore everything we've prepared for you."
                              : "Sit tight while I put the finishing touches on your first set of content."}
                          </Typography>
                        </Box>

                        <Button
                          variant="contained"
                          disabled={!allDataLoaded || isDashboardLoading}
                          startIcon={
                            isDashboardLoading || !allDataLoaded ? (
                              <CircularProgress
                                size={14}
                                sx={{ color: "rgba(255,255,255,0.7)" }}
                              />
                            ) : null
                          }
                          onClick={async () => {
                            if (!allDataLoaded || isDashboardLoading) return;

                            try {
                              setIsDashboardLoading(true);

                              if (audioRef.current) {
                                audioRef.current.pause();
                                audioRef.current.currentTime = 0;
                              }

                              await trainDigitalTwinVoice();

                              localStorage.setItem("activeItem", "authority");

                              navigate("/codegenie/generated-blogs");

                            } catch (err) {
                              console.error(err);
                            } finally {
                              setIsDashboardLoading(false);
                            }
                          }}
                          sx={{
                            height:"42px", px:3, borderRadius:"8px", textTransform:"none",
                            fontWeight:600, fontSize:"14px", color:"#fff", minWidth:"190px",
                            background: allDataLoaded
                              ? "linear-gradient(90deg,#3B82F6,#2563EB)"
                              : "rgba(255,255,255,0.06)",
                            boxShadow: allDataLoaded
                              ? "0 4px 18px rgba(59,130,246,0.35)"
                              : "none",
                            border: !allDataLoaded ? "1px solid rgba(255,255,255,0.1)" : "none",
                            "&:hover": {
                              background: allDataLoaded
                                ? "linear-gradient(90deg,#2563EB,#1D4ED8)"
                                : "rgba(255,255,255,0.06)",
                            },
                            "&.Mui-disabled": {
                              background: "rgba(255,255,255,0.06)",
                              color: "rgba(255,255,255,0.55)",
                              border: "1px solid rgba(255,255,255,0.1)",
                            },
                          }}
                        >
                          {isDashboardLoading
                            ? "Training your Digital Twin..."
                            : allDataLoaded
                              ? "Go to My Dashboard"
                              : "Preparing your content…"}
                        </Button>
                      </Box>

                    </Box>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingCompletePage;